import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

// 5 High-Fidelity UI Patterns from Fig 2
const docxCard = (className: string, t: (k: string) => string) => (
    <div className={`bg-white rounded-[2rem] p-6 shadow-2xl border border-gray-100 flex flex-col relative ${className}`}>
        <div className="absolute inset-4 border border-dashed border-gray-100 rounded-2xl pointer-events-none" />
        <div className="space-y-4 relative z-10 text-left">
            <div className="flex gap-3 items-center">
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center text-lg shadow-sm">📄</div>
                <div>
                    <h4 className="font-bold text-gray-900 text-xs">standard_contract.docx</h4>
                    <p className="text-gray-400 text-[10px]">29kb</p>
                </div>
            </div>
            <div className="flex gap-3 items-center">
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center text-lg shadow-sm">📄</div>
                <div>
                    <h4 className="font-bold text-gray-900 text-xs">review_v1.docx</h4>
                    <p className="text-gray-400 text-[10px]">34kb</p>
                </div>
            </div>
        </div>
        <div className="mt-auto flex gap-2 relative z-10">
            <div className="flex-1 py-2 rounded-lg border border-gray-200 text-[10px] font-black text-gray-900 bg-white flex items-center justify-center uppercase">{t('mq.label.uploadMore')}</div>
            <div className="flex-1 py-2 rounded-lg bg-black text-white text-[10px] font-black flex items-center justify-center uppercase">{t('mq.label.continue')}</div>
        </div>
    </div>
);

const statsCard = (className: string, t: (k: string) => string) => (
    <div className={`bg-white rounded-[2rem] p-8 shadow-2xl border-[1.5px] border-blue-500/20 flex flex-col relative overflow-hidden ${className}`}>
        <div className="bg-gray-50 border border-gray-100 rounded-full px-3 py-1 w-fit text-[9px] font-black text-gray-900 mb-6 uppercase tracking-widest text-left">July, 2026</div>
        <div className="space-y-1 text-left">
            <h3 className="text-4xl font-black text-gray-900 tracking-tighter">$24,630</h3>
            <p className="text-gray-400 text-xs font-medium">{t('mq.label.gain')}</p>
        </div>
        <div className="mt-auto h-16 w-full flex items-end">
            <svg viewBox="0 0 100 20" className="w-full h-full text-blue-500/40 overflow-visible">
                <path d="M0,15 Q25,5 50,15 T100,5" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
            </svg>
        </div>
    </div>
);

const formCard = (className: string, t: (k: string) => string) => (
    <div className={`bg-white rounded-[2rem] p-8 shadow-2xl border border-gray-50 flex flex-col space-y-6 ${className}`}>
        <div className="space-y-4 text-left">
            <div className="space-y-1.5">
                <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest">{t('mq.label.cardNumber')}</label>
                <div className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl flex items-center gap-3">
                    <span className="text-xs text-gray-300 font-mono tracking-widest">0000 0000 0000 0000</span>
                </div>
            </div>
            <div className="flex gap-3">
                <div className="flex-1 space-y-1.5">
                    <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest">{t('mq.label.expires')}</label>
                    <div className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs text-gray-300 font-mono">MM/YY</div>
                </div>
                <div className="flex-1 space-y-1.5">
                    <label className="text-[9px] font-black uppercase text-gray-400 tracking-widest">{t('mq.label.cvc')}</label>
                    <div className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs text-gray-300 font-mono">CVC</div>
                </div>
            </div>
        </div>
        <div className="pt-4 mt-auto">
            <div className="w-full py-3 bg-black text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center shadow-lg">{t('mq.label.pay')} $12.59</div>
        </div>
    </div>
);

const infoCard = (className: string, t: (k: string) => string) => (
    <div className={`bg-white rounded-[2rem] p-8 shadow-2xl border border-gray-100 flex flex-col items-center justify-center text-center space-y-6 ${className}`}>
        <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100 shadow-inner">
            <span className="text-3xl">🛡️</span>
        </div>
        <div className="space-y-2">
            <h4 className="text-lg font-black text-gray-900 tracking-tight">{t('ui.safetyScore')}</h4>
            <p className="text-gray-400 text-[10px] leading-relaxed px-4">{t('ui.scoreDesc')}</p>
        </div>
    </div>
);

const featureCard = (className: string, t: (k: string) => string) => (
    <div className={`bg-white rounded-[2rem] overflow-hidden shadow-2xl flex border border-gray-100 ${className}`}>
        <div className="w-[45%] p-6 bg-gray-50 flex flex-col justify-between border-r border-gray-100 text-left">
            <div className="w-12 h-12 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center text-2xl">⚡</div>
            <div className="space-y-1">
                <h4 className="font-black text-gray-900 text-sm">{t('mq.instant')}</h4>
                <p className="text-gray-400 text-[9px]">{t('mq.instantDesc')}</p>
            </div>
        </div>
        <div className="w-[55%] p-6 flex flex-col justify-between text-left">
            <div className="space-y-2">
                <h5 className="font-bold text-gray-900 text-[10px]">{t('mq.engine')}</h5>
                <p className="text-gray-400 text-[9px] leading-relaxed">{t('mq.engineDesc')}</p>
            </div>
            <div className="w-fit px-4 py-2 rounded-lg border border-gray-200 text-[9px] font-black text-gray-900 uppercase">{t('mq.label.reviewNow')}</div>
        </div>
    </div>
);

export const HomeMarqueeSection: React.FC = () => {
    const { t } = useLanguage();

    const cardConfigs = [
        { render: (c: string) => docxCard(c, t), width: '280px', height: '340px', rotate: '3deg', translateY: '15px' },
        { render: (c: string) => statsCard(c, t), width: '320px', height: '310px', rotate: '-5deg', translateY: '-25px' },
        { render: (c: string) => formCard(c, t), width: '280px', height: '380px', rotate: '4deg', translateY: '20px' },
        { render: (c: string) => infoCard(c, t), width: '260px', height: '320px', rotate: '-7deg', translateY: '-15px' },
        { render: (c: string) => featureCard(c, t), width: '420px', height: '330px', rotate: '5deg', translateY: '10px' },
    ];

    return (
        <section className="bg-transparent py-48 overflow-hidden relative pointer-events-none select-none">
            {/* Infinite Rolling Row - Tight Stacked Scattered Layout */}
            <div className="flex items-center w-max animate-scattered-roll" style={{ perspective: '2000px' }}>
                {/* 3x Duplication for smooth infinite scroll */}
                {[...cardConfigs, ...cardConfigs, ...cardConfigs].map((config, idx) => (
                    <div
                        key={idx}
                        className="gpu-accelerated shrink-0 -ml-24 first:ml-0"
                        style={{
                            width: config.width,
                            height: config.height,
                            transform: `rotate(${config.rotate}) translateY(${config.translateY}) translateZ(${idx % 5 * 10}px)`,
                            zIndex: idx, // Ensure later cards overlap previous ones
                            willChange: 'transform'
                        }}
                    >
                        {config.render('w-full h-full')}
                    </div>
                ))}
            </div>

            <style>{`
                @keyframes scatteredRoll {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-33.33%); }
                }
                .animate-scattered-roll {
                    animation: scatteredRoll 50s linear infinite;
                }
            `}</style>
        </section>
    );
};
